import { useMemo, useRef, useState } from "react";
import { normalizeDefinition, questionVisible, hasContent, buildPeople, summarizeEntry, fmtDate, fmtSeconds } from "@/villa142/config";
import { ContributionDisplay } from "@/villa142/QuestionCard";
import {
  saveFollowup, adminDeleteContribution, adminDeleteQuestionResponses, adminDeleteContributorResponses,
  adminDeleteAllResponses, adminDeleteMedia, adminMediaDownloadUrl, adminImageDownloadUrl, adminVoiceZipUrl, getAdminToken,
} from "@/villa142/api";
import { ConfirmModal } from "@/villa142/admin/ConfirmModal";

const FollowupField = ({ entry }) => {
  const [note, setNote] = useState(entry.followup_note || "");
  const [state, setState] = useState("idle");
  const timer = useRef(null);
  const onChange = (v) => {
    setNote(v);
    setState("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        await saveFollowup(entry.id, v);
        setState("saved");
      } catch {
        setState("error");
      }
    }, 800);
  };
  return (
    <div className="v142-followup">
      <label htmlFor={`fu-${entry.id}`} className="v142-followup-label">
        Follow-up for the space walk
        {state === "saved" && <span className="v142-followup-state"> · Noted</span>}
        {state === "saving" && <span className="v142-followup-state"> · Saving…</span>}
        {state === "error" && <span className="v142-followup-state is-error"> · Could not save yet</span>}
      </label>
      <textarea
        id={`fu-${entry.id}`}
        data-testid={`v142-followup-${entry.key}-${entry.contributor}`}
        className="v142-followup-input"
        rows={2}
        value={note}
        onChange={(e) => onChange(e.target.value)}
        placeholder="A private note for the studio…"
      />
    </div>
  );
};

export default function ReflectionsView({ data, reload }) {
  const [contribFilter, setContribFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [confirm, setConfirm] = useState(null); // {title, body, action}
  const [busy, setBusy] = useState(false);
  const token = getAdminToken();

  const people = useMemo(() => buildPeople(data.contributors, true), [data]);

  const def = useMemo(
    () => normalizeDefinition({ chapters: data.definition.chapters, studies: data.studies, version: data.definition.version }),
    [data]
  );

  const entriesMap = useMemo(() => {
    const map = {};
    data.entries.forEach((e) => {
      (map[e.key] = map[e.key] || {})[e.contributor] = e;
    });
    return map;
  }, [data]);

  const contributorIds = useMemo(() => {
    const ids = [...people.orderAll];
    data.entries.forEach((e) => { if (!ids.includes(e.contributor)) ids.push(e.contributor); });
    return ids;
  }, [people, data]);

  const runConfirmed = async () => {
    setBusy(true);
    try {
      await confirm.action();
      setConfirm(null);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const contribCounts = contributorIds.map((c) => ({ c, n: data.entries.filter((e) => e.contributor === c && hasContent(e)).length }));
  const totalWithContent = data.entries.filter((e) => hasContent(e)).length;
  const hasVoiceNotes = data.entries.some((e) => (e.media || []).some((m) => m.kind === "audio"));

  const audioExtras = (m) => (
    <div className="v142-audio-extras">
      {m.transcript && (
        <p className="v142-transcript" data-testid={`v142-transcript-${m.id}`}>“{m.transcript}”</p>
      )}
      <div className="v142-recorder-actions">
        <a className="v142-link" href={adminMediaDownloadUrl(m.id)} data-testid={`v142-download-note-${m.id}`}>
          Download{m.duration != null ? ` (${fmtSeconds(m.duration)})` : ""}
        </a>
        <button
          type="button"
          className="v142-link v142-danger-link"
          data-testid={`v142-delete-note-${m.id}`}
          onClick={() =>
            setConfirm({
              title: "Delete this voice note",
              body: "Delete this recording and its transcript? This cannot be undone.",
              action: () => adminDeleteMedia(m.id),
            })
          }
        >
          Delete note
        </button>
      </div>
    </div>
  );

  const imageExtras = (m) => (
    <a className="v142-link v142-img-download" href={adminImageDownloadUrl(m.id)} data-testid={`v142-download-image-${m.id}`} download>
      Download original
    </a>
  );

  const exportJson = () => {
    const payload = {
      project: "Villa 142 — A private design reflection",
      exported_at: new Date().toISOString(),
      questionnaire_version: data.definition.version,
      submitted: data.submitted,
      submitted_at: data.submitted_at,
      shared_at: data.shared_at,
      last_updated_after_share_at: data.last_updated_after_share_at,
      chapters: def.chapters.map((ch) => ({
        title: ch.title,
        questions: ch.questions.map((q) => ({
          id: q.id,
          code: q.code || null,
          question: q.label,
          contributions: contributorIds.filter((c) => hasContent(entriesMap[q.id]?.[c])).map((c) => {
            const e = entriesMap[q.id][c];
            return {
              contributor: people.name(c),
              summary: summarizeEntry(q, e),
              value: e.value,
              media: (e.media || []).map((m) => ({ id: m.id, kind: m.kind, caption: m.caption || null, item_id: m.item_id || null, duration: m.duration ?? null, transcript: m.transcript || null })),
              followup_note: e.followup_note || null,
              question_version: e.question_version || null,
              created_at: e.created_at,
              updated_at: e.updated_at,
            };
          }),
        })),
      })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "villa142-reflections.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="v142-print-area">
      <div className="v142-admin-controls">
        <div className="v142-admin-filters">
          <label className="v142-mini-label" htmlFor="v142-filter-contrib">Contributor</label>
          <select id="v142-filter-contrib" data-testid="v142-filter-contributor" className="v142-select" value={contribFilter} onChange={(e) => setContribFilter(e.target.value)}>
            <option value="all">All</option>
            {contributorIds.map((c) => <option key={c} value={c}>{people.name(c)}</option>)}
          </select>
          <label className="v142-mini-label" htmlFor="v142-filter-state">State</label>
          <select id="v142-filter-state" data-testid="v142-filter-state" className="v142-select" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
            <option value="all">All questions</option>
            <option value="answered">With reflections</option>
            <option value="open">Still open</option>
          </select>
        </div>
        <div className="v142-admin-actions">
          {hasVoiceNotes && (
            <a className="v142-btn v142-btn-small" href={adminVoiceZipUrl()} data-testid="v142-export-zip-btn">
              Download all voice notes (.zip)
            </a>
          )}
          <button className="v142-btn v142-btn-small" data-testid="v142-export-json-btn" onClick={exportJson}>Export JSON</button>
          <button className="v142-btn v142-btn-small" data-testid="v142-print-btn" onClick={() => window.print()}>Print</button>
        </div>
      </div>

      <div className="v142-admin-meta">
        <span data-testid="v142-admin-share-status">{data.submitted ? `Shared by client · ${fmtDate(data.shared_at || data.submitted_at)}` : "Draft — not yet shared by client"}</span>
        {data.last_updated_after_share_at && <span data-testid="v142-admin-updated-after-share">Updated after sharing · {fmtDate(data.last_updated_after_share_at)}</span>}
        <span>Last activity {data.last_updated ? fmtDate(data.last_updated) : "—"}</span>
        <span>Questionnaire v{data.definition.version}</span>
      </div>

      {def.chapters.map((ch) => {
        const visible = ch.questions.filter((q) => questionVisible(q, entriesMap));
        const rows = visible.filter((q) => {
          const answered = contributorIds.some((c) => hasContent(entriesMap[q.id]?.[c]));
          if (stateFilter === "answered") return answered;
          if (stateFilter === "open") return !answered;
          return true;
        });
        if (!rows.length) return null;
        return (
          <div className="v142-review-chapter" key={ch.id}>
            <h2 className="v142-review-chtitle">{ch.title}</h2>
            {rows.map((q) => {
              const contribs = contributorIds.filter(
                (c) => hasContent(entriesMap[q.id]?.[c]) && (contribFilter === "all" || contribFilter === c)
              );
              return (
                <div className="v142-review-row" key={q.id}>
                  <div className="v142-review-body">
                    <p className="v142-review-q">{q.code && <span className="v142-qcode">{q.code}</span>}{q.label}</p>
                    {contribs.length === 0 && <p className="v142-review-a is-empty">Still open</p>}
                    {contribs.map((c) => {
                      const e = entriesMap[q.id][c];
                      return (
                        <ContributionDisplay key={c} question={q} entry={e} token={token} people={people} audioExtras={audioExtras} imageExtras={imageExtras}>
                          <FollowupField entry={e} />
                          <button
                            type="button"
                            className="v142-link v142-danger-link"
                            data-testid={`v142-delete-entry-${q.id}-${c}`}
                            onClick={() =>
                              setConfirm({
                                title: "Delete one contribution",
                                body: `Delete ${people.entryLabel(c)} on "${q.label}"?`,
                                action: () => adminDeleteContribution(e.id),
                              })
                            }
                          >
                            Delete this contribution
                          </button>
                        </ContributionDisplay>
                      );
                    })}
                    {contributorIds.some((c) => hasContent(entriesMap[q.id]?.[c])) && (
                      <button
                        type="button"
                        className="v142-link v142-danger-link"
                        data-testid={`v142-clear-question-${q.id}`}
                        onClick={() =>
                          setConfirm({
                            title: "Clear one question's reflections",
                            body: `Delete every contribution to "${q.label}"?`,
                            action: () => adminDeleteQuestionResponses(q.id),
                          })
                        }
                      >
                        Clear all responses to this question
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      <div className="v142-danger-zone" data-testid="v142-danger-zone">
        <h3 className="v142-danger-title">Reset controls</h3>
        <p className="v142-support">Scoped, intentional deletion. Export a copy first if in doubt.</p>
        <div className="v142-danger-row">
          {contribCounts.filter(({ n }) => n > 0).map(({ c, n }) => (
            <button
              key={c}
              type="button"
              className="v142-btn v142-btn-small"
              data-testid={`v142-clear-contributor-${c}`}
              onClick={() =>
                setConfirm({
                  title: `Clear ${people.name(c)}'s reflections`,
                  body: `Delete all ${n} of ${people.name(c)}'s contributions across every question?`,
                  action: () => adminDeleteContributorResponses(c),
                })
              }
            >
              Clear {people.name(c)} ({n})
            </button>
          ))}
          <button
            type="button"
            className="v142-btn v142-btn-small v142-btn-danger"
            disabled={!totalWithContent && !data.submitted}
            data-testid="v142-clear-project"
            onClick={() =>
              setConfirm({
                title: "Clear the full project record",
                body: `Delete ALL ${data.entries.length} saved contributions (including uploads and voice notes) and reset the shared/submission state for Villa 142?`,
                confirmLabel: "Delete everything",
                action: () => adminDeleteAllResponses(),
              })
            }
          >
            Clear full project record
          </button>
        </div>
      </div>

      <ConfirmModal
        open={Boolean(confirm)}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel={confirm?.confirmLabel || "Delete"}
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={runConfirmed}
      />
    </div>
  );
}
